import { FunctionComponent, useEffect } from 'react'
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { getCityNamesByLang } from 'utils/city-names'
import { BootstrapDialog } from 'components/BootstrapDialog'
import { RequiredStar } from 'components/RequiredStarSpan'
import { DialogCloseButton } from 'components/DialogCloseButton'
import { FileUpload } from 'components/FileUpload'
import { Download } from '@mui/icons-material'
import { IProcurementPlanItem } from 'types/procurementPlans/procurementPlanItems'
import { ETenderType } from 'types/tender'
import { useGetFileQuery } from 'services/file'

interface FormData {
  rowNum: number
  name: string
  method: string
  announcementMonth: string
  city: string
  executionAddress: string
  executionDurationInDays: string
}

enum ProcurementMethod {
  OPEN_TENDER = 'OPEN_TENDER',
  PRICE_INQUIRY = 'PRICE_INQUIRY',
  TWO_STAGE_TENDER = 'TWO_STAGE_TENDER',
  SINGLE_SOURCE = 'SINGLE_SOURCE',
}

interface Props {
  onProcurementPlanItemCreate: (item: IProcurementPlanItem) => void
  handleClose: () => void
  plan: IProcurementPlanItem | null
}

export const ProcurementForm: FunctionComponent<Props> = ({
  handleClose,
  onProcurementPlanItemCreate,
  plan,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  const {
    handleSubmit,
    register,
    formState: { errors },
    control,
    setValue,
    reset,
  } = useForm<FormData>({
    reValidateMode: 'onSubmit',
    defaultValues: {
      name: plan ? plan.name : '',
      method: plan ? plan.method : '',
      announcementMonth: plan ? plan.announcementMonth : '',
      city: plan ? plan.city : '',
      executionAddress: plan ? plan.deliveryAddress : '',
      executionDurationInDays: plan ? String(plan.executionDurationInDays) : '',
    },
  })
  const isEdit = !!plan

  useEffect(() => {
    return () => {
      reset()
    }
  }, [reset])

  const onSubmit = (data: FormData) => {
    onProcurementPlanItemCreate({
      ...(plan ?? {}),
      rowNum: data.rowNum,
      name: data.name,
      method: data.method as ETenderType,
      announcementMonth: data.announcementMonth,
      city: data.city,
      executionDurationInDays: data.executionDurationInDays,
      deliveryAddress: data.executionAddress,
    })
    handleClose()
  }

  return (
    <BootstrapDialog open onClose={handleClose}>
      <DialogTitle sx={{ m: 0, p: 2 }}>{t('procurement-plan')}</DialogTitle>

      <DialogCloseButton handleClose={handleClose} />

      <DialogContent dividers>
        <form onSubmit={handleSubmit(onSubmit)} id="procurement-plan-form">
          <FormControl sx={{ minWidth: 60 }} fullWidth margin="normal">
            <TextField
              label={
                <>
                  {t('predmet-row-num')} <RequiredStar>*</RequiredStar>
                </>
              }
              variant="outlined"
              error={!!errors.name}
              {...register('rowNum', { required: t('required') })}
            />
            {errors.name && (
              <FormHelperText error>{errors.name?.message}</FormHelperText>
            )}
          </FormControl>
          <FormControl sx={{ minWidth: 120 }} fullWidth margin="normal">
            <TextField
              label={
                <>
                  {t('predmet-name')} <RequiredStar>*</RequiredStar>
                </>
              }
              variant="outlined"
              error={!!errors.name}
              {...register('name', { required: t('required') })}
            />
            {errors.name && (
              <FormHelperText error>{errors.name?.message}</FormHelperText>
            )}
          </FormControl>
          <FormControl sx={{ minWidth: 120 }} fullWidth margin="normal">
            <InputLabel id="method-select-helper-label">
              {t('procurementMethod')} <RequiredStar>*</RequiredStar>
            </InputLabel>

            <Controller
              name="method"
              control={control}
              rules={{ required: t('required') }}
              render={({ field }) => (
                <Select
                  {...field}
                  error={!!errors.method}
                  labelId="method-select-helper-label"
                  label={
                    <>
                      {t('procurementMethod')} <RequiredStar>*</RequiredStar>
                    </>
                  }
                >
                  {Object.keys(ProcurementMethod).map((method) => (
                    <MenuItem value={method} key={method}>
                      {t(method)}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />

            {errors.method && (
              <FormHelperText error>{errors.method?.message}</FormHelperText>
            )}
          </FormControl>

          <FormControl sx={{ minWidth: 120 }} fullWidth margin="normal">
            <InputLabel id="month-select-helper-label">
              {t('anouncement-month')} <RequiredStar>*</RequiredStar>
            </InputLabel>

            <Controller
              name="announcementMonth"
              control={control}
              rules={{ required: t('required') }}
              render={({ field }) => (
                <Select
                  {...field}
                  error={!!errors.announcementMonth}
                  labelId="month-select-helper-label"
                  label={
                    <>
                      {t('anouncement-month')} <RequiredStar>*</RequiredStar>
                    </>
                  }
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((month) => {
                    return (
                      <MenuItem key={month} value={month}>
                        {t(`month-${month}`)}
                      </MenuItem>
                    )
                  })}
                </Select>
              )}
            />

            {errors.announcementMonth && (
              <FormHelperText error>
                {errors.announcementMonth?.message}
              </FormHelperText>
            )}
          </FormControl>

          <FormControl sx={{ minWidth: 120 }} fullWidth margin="normal">
            <InputLabel id="city-select-helper-label">
              {t('city-district')} <RequiredStar>*</RequiredStar>
            </InputLabel>

            <Controller
              name="city"
              control={control}
              rules={{ required: t('required') }}
              render={({ field }) => (
                <Select
                  {...field}
                  error={!!errors.city}
                  labelId="city-select-helper-label"
                  label={
                    <>
                      {t('city-district')} <RequiredStar>*</RequiredStar>
                    </>
                  }
                >
                  {getCityNamesByLang(language).map((city, idx) => {
                    return (
                      <MenuItem key={idx} value={city}>
                        {city}
                      </MenuItem>
                    )
                  })}
                </Select>
              )}
            />

            {errors.city && (
              <FormHelperText error>{errors.city?.message}</FormHelperText>
            )}
          </FormControl>

          <FormControl sx={{ minWidth: 120 }} fullWidth margin="normal">
            <TextField
              label={
                <>
                  {t('execution-address')} <RequiredStar>*</RequiredStar>
                </>
              }
              variant="outlined"
              error={!!errors.executionAddress}
              {...register('executionAddress', { required: t('required') })}
            />

            {errors.executionAddress && (
              <FormHelperText error>
                {errors.executionAddress?.message}
              </FormHelperText>
            )}
          </FormControl>

          <FormControl sx={{ minWidth: 120 }} fullWidth margin="normal">
            <TextField
              label={
                <>
                  {t('execution-duration-in-days')} <RequiredStar>*</RequiredStar>
                </>
              }
              variant="outlined"
              error={!!errors.executionDurationInDays}
              {...register('executionDurationInDays', { required: t('required') })}
            />

            {errors.executionDurationInDays && (
              <FormHelperText error>
                {errors.executionDurationInDays?.message}
              </FormHelperText>
            )}
          </FormControl>
        </form>
      </DialogContent>
      <DialogActions>
        <Button
          autoFocus
          type="submit"
          variant="contained"
          style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
          form="procurement-plan-form"
        >
          {isEdit ? t('save') : t('add-predmet')}
        </Button>
      </DialogActions>
    </BootstrapDialog>
  )
}
